import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Visible label. The DS treats a placeholder as example content, never as the
   * label — when a field has no visible label it must still carry an
   * `aria-label`, which `Field` below wires up for you.
   */
  label?: string;
  helper?: string;
  /** Replaces `helper` and marks the field invalid; announced once. */
  error?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, helper, error, id, ...props }, ref) => {
    const generated = React.useId();
    const inputId = id ?? generated;
    const describedBy = error ? `${inputId}-error` : helper ? `${inputId}-helper` : undefined;

    const field = (
      <input
        type={type}
        id={inputId}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          "flex h-9 w-full rounded-sm border border-control-border bg-control px-3 text-sm transition-colors pointer-coarse:h-11 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-fg-muted focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-error",
          className,
        )}
        {...props}
      />
    );

    if (!label && !helper && !error) return field;

    return (
      <div className="flex flex-col gap-1">
        {label ? (
          <label htmlFor={inputId} className="text-xs font-medium text-fg-body">
            {label}
          </label>
        ) : null}
        {field}
        {error ? (
          <p id={`${inputId}-error`} role="alert" className="text-xs text-error">
            {error}
          </p>
        ) : helper ? (
          <p id={`${inputId}-helper`} className="text-xs text-fg-muted">
            {helper}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
